import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { useMeasure } from 'src/hooks'

export interface OverflowListProps<T> {
  items: T[]
  itemRenderer: (item: T, index: number) => ReactNode
  overflowRenderer: (items: T[]) => ReactNode
  /** Always keep at least this many items visible, even if they overflow. */
  minVisibleItems?: number
  className?: string
}

/**
 * Renders as many items as fit on a single line, collapsing the rest behind an
 * overflow node ("+N"). Stable by construction: all items are measured once
 * from a hidden full render, and how many are visible is derived purely from
 * the container width — the visible count never feeds back into what we
 * measure, so there is no resize/re-measure loop.
 */
export default function OverflowList<T>({
  items,
  minVisibleItems = 1,
  className = '',
  overflowRenderer,
  itemRenderer,
}: OverflowListProps<T>) {
  const [containerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>()

  // Natural width of every item + the overflow node, measured from a hidden
  // full render. Recomputed only when the items change, never on resize.
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [widths, setWidths] = useState<{ items: number[]; overflow: number }>({
    items: [],
    overflow: 0,
  })

  useLayoutEffect(() => {
    const node = measureRef.current
    if (!node) return
    const children = Array.from(node.children) as HTMLElement[]
    const overflow = children[children.length - 1]
    setWidths({
      items: children.slice(0, items.length).map((c) => c.offsetWidth),
      overflow: overflow ? overflow.offsetWidth : 0,
    })
  }, [items])

  const GAP = 4

  const visibleCount = (() => {
    if (!containerWidth || widths.items.length !== items.length) {
      return items.length
    }
    let used = 0
    let count = 0
    for (let i = 0; i < items.length; i += 1) {
      const next = used + (i > 0 ? GAP : 0) + widths.items[i]
      // Reserve room for the overflow node unless this is the last item.
      const reserve = i === items.length - 1 ? 0 : GAP + widths.overflow
      if (count >= minVisibleItems && next + reserve > containerWidth) {
        break
      }
      used = next
      count += 1
    }
    return Math.max(count, Math.min(minVisibleItems, items.length))
  })()

  const visible = items.slice(0, visibleCount)
  const overflow = items.slice(visibleCount)

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: GAP,
    minWidth: 0,
    overflow: 'hidden',
  }

  return (
    <div ref={containerRef} className={className} style={{ minWidth: 0 }}>
      {/* Hidden full render used only to measure natural widths. */}
      <div
        ref={measureRef}
        style={{
          ...rowStyle,
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          height: 0,
          overflow: 'visible',
        }}
        aria-hidden
      >
        {items.map(itemRenderer)}
        {overflowRenderer(items)}
      </div>

      <div style={rowStyle}>
        {visible.map(itemRenderer)}
        {overflow.length > 0 && overflowRenderer(overflow)}
      </div>
    </div>
  )
}
